import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import debounce from "lodash/debounce";
import {
  assignCourse,
  listAssignmentAcademies,
  findAssignableCourses,
  type CourseAssignment,
  type SelectOption,
} from "../../../api/academyCourseAssignments/courseAssignmentsService";

interface FieldErrors {
  academy?: string;
  course?: string;
  fullName?: string;
  phoneNo?: string;
}

interface UseCourseAssignmentDrawerParams {
  show: boolean;
  onHide: () => void;
  onSuccess?: () => void;
}

const COUNTRY_CODE = 91;

export function useCourseAssignmentDrawer({
  show,
  onHide,
  onSuccess,
}: UseCourseAssignmentDrawerParams) {
  // ── dropdown data ──────────────────────────────────────────────────────────
  const [academyOptions, setAcademyOptions] = useState<SelectOption[]>([]);
  const [isLoadingAcademies, setIsLoadingAcademies] = useState(false);

  // ── field state ────────────────────────────────────────────────────────────
  const [selectedAcademy, setSelectedAcademyState] = useState<SelectOption | null>(null);
  const [selectedCourse, setSelectedCourseState] = useState<SelectOption | null>(null);
  const [fullName, setFullName] = useState("");
  const [phoneNo, setPhoneNo] = useState("");

  // ── validation / submission ────────────────────────────────────────────────
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── reset and load academies when drawer opens ─────────────────────────────
  useEffect(() => {
    if (!show) return;

    setFieldErrors({});
    setSubmitError(null);
    setSelectedAcademyState(null);
    setSelectedCourseState(null);
    setFullName("");
    setPhoneNo("");

    let cancelled = false;

    const loadAcademies = async () => {
      setIsLoadingAcademies(true);
      try {
        const options = await listAssignmentAcademies();
        if (!cancelled) setAcademyOptions(options);
      } catch {
        if (!cancelled) setAcademyOptions([]);
      } finally {
        if (!cancelled) setIsLoadingAcademies(false);
      }
    };

    loadAcademies();

    return () => {
      cancelled = true;
    };
  }, [show]);

  // ── debounced course loader (300 ms) ──────────────────────────────────────
  type LoadCallback = (opts: SelectOption[]) => void;
  const academyIdRef = useRef<string | undefined>(undefined);
  academyIdRef.current = selectedAcademy?.value;

  const debouncedSearch = useMemo(
    () =>
      debounce((inputValue: string, cb: LoadCallback) => {
        const id = academyIdRef.current;
        if (!id) { cb([]); return; }
        findAssignableCourses(id, inputValue).then(cb).catch(() => cb([]));
      }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    debouncedSearch.cancel();
  }, [selectedAcademy?.value, debouncedSearch]);

  const loadCourseOptions = useCallback(
    (inputValue: string, callback: LoadCallback) => {
      const id = academyIdRef.current;
      if (!id) { callback([]); return; }
      if (!inputValue) {
        findAssignableCourses(id, "").then(callback).catch(() => callback([]));
      } else {
        debouncedSearch(inputValue, callback);
      }
    },
    [debouncedSearch]
  );

  // ── field handlers ─────────────────────────────────────────────────────────
  const setSelectedAcademy = useCallback((opt: SelectOption | null) => {
    setSelectedAcademyState(opt);
    setSelectedCourseState(null);
    setFieldErrors((prev) => ({ ...prev, academy: undefined }));
  }, []);

  const setSelectedCourse = useCallback((opt: SelectOption | null) => {
    setSelectedCourseState(opt);
    setFieldErrors((prev) => ({ ...prev, course: undefined }));
  }, []);

  const handleFullNameChange = useCallback((v: string) => {
    setFullName(v);
    setFieldErrors((prev) => ({ ...prev, fullName: undefined }));
  }, []);

  const handlePhoneNoChange = useCallback((v: string) => {
    setPhoneNo(v.replace(/\D/g, "").slice(0, 10));
    setFieldErrors((prev) => ({ ...prev, phoneNo: undefined }));
  }, []);

  // ── validation ─────────────────────────────────────────────────────────────
  const validate = (): boolean => {
    const errors: FieldErrors = {};
    if (!selectedAcademy) errors.academy = "Academy is required.";
    if (!selectedCourse) errors.course = "Course is required.";
    if (!fullName.trim()) errors.fullName = "Full name is required.";
    if (!phoneNo.trim()) {
      errors.phoneNo = "Phone number is required.";
    } else if (phoneNo.trim().length < 10) {
      errors.phoneNo = "Phone number must be at least 10 digits.";
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (!validate()) return;

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      await assignCourse({
        courseId: selectedCourse!.value,
        countryCode: COUNTRY_CODE,
        phoneNumber: Number(phoneNo.trim()),
        fullName: fullName.trim(),
      });
      onSuccess?.();
      onHide();
    } catch (err: unknown) {
      let message = "Failed to create assignment.";
      if (err instanceof Error) message = err.message;
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAcademy, selectedCourse, fullName, phoneNo, onHide, onSuccess]);

  return {
    functions: {
      handleFullNameChange,
      handlePhoneNoChange,
      handleSubmit,
      setSelectedAcademy,
      setSelectedCourse,
      loadCourseOptions,
    },
    values: {
      academyOptions,
      selectedAcademy,
      selectedCourse,
      fullName,
      phoneNo,
      fieldErrors,
      submitError,
    },
    loading: {
      isSubmitting,
      isLoadingAcademies,
    },
  };
}

// Kept for backwards compat with the page/hook imports
export type CourseAssignmentDrawerMode = "create";
